/* eslint-disable no-restricted-syntax */
import { formatDistanceToNow } from 'date-fns';
import { AlertCircle, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { CitationViewer } from '@/components/agent-chat/citations/CitationViewer';
import { TaskDetail } from '@/components/tasks/task-detail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useGetArtifactUnpackedQuery } from '@/services/api/artifactService';
import { useGetBlobWithMarkdownContentQuery } from '@/services/api/blobService';
import { useGetStaffNoteQuery } from '@/services/api/staffNotesService';
import { useGetTranscriptSegmentsQuery } from '@/services/api/transcriptService';
import { TMaterializedCitationBlock } from '@/services/types/agent-chat-types';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { MarkdownBlock } from '@/services/types/blob-types';
import type { WikiCitation } from '@/services/types/matter-wiki-types';

import {
  extractCitationTargetId,
  normalizeCitationSourceType,
} from './utils/citations';

interface WikiCitationDrawerProps {
  matterId: string;
  citation: WikiCitation | null;
  onClose: () => void;
}

interface ArtifactCitationContentProps {
  matterId: string;
  artifactId: string;
  citation: WikiCitation;
  isResizing: boolean;
}

interface BlobCitationContentProps {
  matterId: string;
  blobId: string;
  citation: WikiCitation;
  pdfUrl: string | null;
  showPdfHighlighter: boolean;
  isResizing: boolean;
}

interface TranscriptCitationContentProps {
  matterId: string;
  conversationId: string;
  citation: WikiCitation;
}

const EmptyState = ({ message }: { message: string }) => (
  <div className="flex h-full items-center justify-center px-6 py-10 text-center text-sm text-zinc-500">
    {message}
  </div>
);

const LoaderState = () => (
  <div className="space-y-3 p-4">
    <Skeleton className="h-5 w-2/3" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-11/12" />
    <Skeleton className="h-4 w-4/5" />
  </div>
);

const ErrorState = ({ message }: { message: string }) => (
  <div className="m-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
    {message}
  </div>
);

function parsePageNumberFromBlockId(blockId: string): number | null {
  const prefixedMatch = blockId.match(/\/page\/(\d+)(?:\/|$)/);
  if (prefixedMatch) {
    return Number(prefixedMatch[1]);
  }

  const plainMatch = blockId.match(/^page\/(\d+)(?:\/|$)/);
  if (plainMatch) {
    return Number(plainMatch[1]);
  }

  return null;
}

function buildPageDimensionsMap(
  blocks: MarkdownBlock[],
): Record<number, { width: number; height: number }> {
  const pageDimensionsByNumber: Record<number, { width: number; height: number }> = {};

  blocks.forEach((block) => {
    const pageNum = parsePageNumberFromBlockId(block.block_id);
    if (pageNum === null) {
      return;
    }

    const bbox = block.bounding_box ?? [];
    if (bbox.length < 4) {
      return;
    }

    const isPageBlock =
      /^\/?page\/\d+$/.test(block.block_id) || /^page\/\d+$/.test(block.block_id);

    if (isPageBlock) {
      pageDimensionsByNumber[pageNum] = {
        width: bbox[2],
        height: bbox[3],
      };
    }
  });

  return pageDimensionsByNumber;
}

function toCitationLevel(blockId: string): 'document' | 'page' | 'section' {
  if (/^\/?page\/\d+$/.test(blockId) || /^page\/\d+$/.test(blockId)) {
    return 'page';
  }

  if (blockId === 'document') {
    return 'document';
  }

  return 'section';
}

interface DocumentSegment {
  blockId: string;
  content: string;
  isCited: boolean;
}

function lookupBlock(
  blockMap: Map<string, MarkdownBlock>,
  src: string,
): MarkdownBlock | undefined {
  return blockMap.get(src) ?? blockMap.get(src.startsWith('/') ? src.slice(1) : `/${src}`);
}

function blockIdMatches(a: string, b: string): boolean {
  return a === b || a === `/${b}` || `/${a}` === b;
}

/**
 * Walk the content-ref tree starting from a block's content, collecting
 * leaf text into an ordered list of segments tagged with their block ID.
 */
function collectSegments(
  content: string,
  blockId: string,
  blockMap: Map<string, MarkdownBlock>,
  citedBlockId: string | null,
  segments: DocumentSegment[],
  depth = 0,
): void {
  if (depth > 8) return;

  const refRe = /<content-ref\s+src="([^"]+)"[^>]*>\s*<\/content-ref>/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let hasRefs = false;

  // eslint-disable-next-line no-cond-assign
  while ((match = refRe.exec(content)) !== null) {
    hasRefs = true;

    const textBefore = content.slice(lastIndex, match.index).trim();
    if (textBefore) {
      segments.push({
        blockId,
        content: textBefore,
        isCited: citedBlockId !== null && blockIdMatches(blockId, citedBlockId),
      });
    }

    const referenced = lookupBlock(blockMap, match[1]);
    if (referenced) {
      collectSegments(referenced.content, referenced.block_id, blockMap, citedBlockId, segments, depth + 1);
    }

    lastIndex = match.index + match[0].length;
  }

  if (!hasRefs) {
    const trimmed = content.trim();
    if (trimmed) {
      segments.push({
        blockId,
        content: trimmed,
        isCited: citedBlockId !== null && blockIdMatches(blockId, citedBlockId),
      });
    }
  } else {
    const textAfter = content.slice(lastIndex).trim();
    if (textAfter) {
      segments.push({
        blockId,
        content: textAfter,
        isCited: citedBlockId !== null && blockIdMatches(blockId, citedBlockId),
      });
    }
  }
}

/**
 * Build an ordered list of document segments by walking the content-ref tree.
 * Merges adjacent segments that share the same cited status for cleaner rendering.
 */
function buildDocumentSegments(
  blocks: MarkdownBlock[],
  citedBlockId: string | null,
): DocumentSegment[] {
  const blockMap = new Map(blocks.map((b) => [b.block_id, b]));

  // Find root blocks — those not referenced by any other block's content-refs.
  const referencedIds = new Set<string>();
  for (const block of blocks) {
    const refRe = /<content-ref\s+src="([^"]+)"/g;
    let m: RegExpExecArray | null;
    // eslint-disable-next-line no-cond-assign
    while ((m = refRe.exec(block.content)) !== null) {
      const src = m[1];
      referencedIds.add(src);
      referencedIds.add(src.startsWith('/') ? src.slice(1) : `/${src}`);
    }
  }

  const roots = blocks
    .filter((b) => !referencedIds.has(b.block_id))
    .sort((a, b) => a.order - b.order);

  const segments: DocumentSegment[] = [];
  for (const root of roots) {
    collectSegments(root.content, root.block_id, blockMap, citedBlockId, segments);
  }

  // Merge adjacent segments with the same cited status.
  const merged: DocumentSegment[] = [];
  for (const seg of segments) {
    const last = merged[merged.length - 1];
    if (last && last.isCited === seg.isCited) {
      last.content += `\n\n${  seg.content}`;
    } else {
      merged.push({ ...seg });
    }
  }

  return merged;
}

const BLOCK_PROSE_CLASSES = [
  'prose prose-zinc prose-xs max-w-none',
  'prose-headings:font-semibold prose-headings:text-zinc-800',
  'prose-h1:text-sm prose-h2:text-xs prose-h3:text-xs',
  'prose-p:text-xs prose-p:text-zinc-700 prose-p:leading-relaxed prose-p:my-1',
  'prose-strong:text-zinc-800 prose-strong:font-semibold',
  'prose-table:text-xs',
  'prose-th:text-zinc-500 prose-th:font-medium prose-th:text-left prose-th:pb-1.5 prose-th:border-b prose-th:border-zinc-200',
  'prose-td:py-1.5 prose-td:border-b prose-td:border-zinc-50',
  'prose-li:text-xs prose-li:text-zinc-700',
].join(' ');

function materializePdfCitationBlocks(
  blocks: MarkdownBlock[],
): TMaterializedCitationBlock[] {
  const pageDimensionsByNumber = buildPageDimensionsMap(blocks);

  return blocks
    .map((block) => {
      const pageNumber = parsePageNumberFromBlockId(block.block_id);
      const bbox = block.bounding_box ?? [];

      if (pageNumber === null || bbox.length < 4) {
        return null;
      }

      const pageDimensions = pageDimensionsByNumber[pageNumber];
      if (!pageDimensions) {
        return null;
      }

      return {
        block_id: block.block_id,
        level: toCitationLevel(block.block_id),
        bounding_box: {
          x1: bbox[0],
          y1: bbox[1],
          x2: bbox[2],
          y2: bbox[3],
        },
        page_dimensions: {
          width: pageDimensions.width,
          height: pageDimensions.height,
        },
        page_number: pageNumber,
      } satisfies TMaterializedCitationBlock;
    })
    .filter((block): block is TMaterializedCitationBlock => block !== null);
}

const BlobCitationContent = ({
  matterId,
  blobId,
  citation,
  pdfUrl,
  showPdfHighlighter,
  isResizing,
}: BlobCitationContentProps) => {
  const { data, isLoading, isError } = useGetBlobWithMarkdownContentQuery(
    {
      matterId,
      blobId,
    },
    {
      skip: !blobId,
    },
  );

  const targetBlockId =
    citation.locator_type === 'block' ? citation.locator_start : null;

  const citedRef = useRef<HTMLDivElement | null>(null);

  const orderedBlocks = useMemo(() => {
    const blocks = data?.blocks ?? [];
    return [...blocks].sort((a, b) => a.order - b.order);
  }, [data?.blocks]);

  const pdfCitations = useMemo(
    () => materializePdfCitationBlocks(orderedBlocks),
    [orderedBlocks],
  );

  const [selectedBlockId, setSelectedBlockId] = useState<string>('');

  const normalizedSelectedBlockId = useMemo(() => {
    if (!selectedBlockId) return '';
    const candidates = selectedBlockId.startsWith('/')
      ? [selectedBlockId, selectedBlockId.slice(1)]
      : [selectedBlockId, `/${selectedBlockId}`];
    const matched = pdfCitations.find((c) => candidates.includes(c.block_id));
    return matched?.block_id ?? selectedBlockId;
  }, [selectedBlockId, pdfCitations]);

  const visiblePdfCitations = useMemo(() => {
    if (!normalizedSelectedBlockId) return [];
    const matched = pdfCitations.find((c) => c.block_id === normalizedSelectedBlockId);
    return matched ? [matched] : [];
  }, [normalizedSelectedBlockId, pdfCitations]);

  const canRenderPdfViewer =
    showPdfHighlighter && Boolean(pdfUrl) && visiblePdfCitations.length > 0;

  const documentSegments = useMemo(
    () => buildDocumentSegments(orderedBlocks, targetBlockId ?? null),
    [orderedBlocks, targetBlockId],
  );

  useEffect(() => {
    if (targetBlockId) {
      setSelectedBlockId(targetBlockId);
    }
  }, [targetBlockId]);

  useEffect(() => {
    if (!canRenderPdfViewer && citedRef.current) {
      citedRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [canRenderPdfViewer, documentSegments]);

  if (isLoading) {
    return <LoaderState />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load blob citation context." />;
  }

  if (orderedBlocks.length === 0) {
    return (
      <div className="p-4">
        {data.markdown_content ? (
          <div className={cn(BLOCK_PROSE_CLASSES, 'max-h-[60vh] overflow-auto p-3')}>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
              {data.markdown_content}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="text-xs text-zinc-500">No structured content is available for this blob.</p>
        )}
      </div>
    );
  }

  if (canRenderPdfViewer && pdfUrl) {
    return (
      <div className="h-full">
        {isResizing ? (
          <div className="flex h-full items-center justify-center bg-zinc-50">
            <p className="text-xs text-zinc-400">Resizing...</p>
          </div>
        ) : (
          <CitationViewer
            url={pdfUrl}
            citations={visiblePdfCitations}
            selectedCitationId={normalizedSelectedBlockId}
            setSelectedCitationId={setSelectedBlockId}
          />
        )}
      </div>
    );
  }

  return (
    <div className={cn('p-4', BLOCK_PROSE_CLASSES, 'max-h-[60vh] overflow-y-auto')}>
      {documentSegments.map((segment, i) => (
        <div
          key={i}
          ref={segment.isCited ? citedRef : undefined}
          className={cn(
            'border-l-2 pl-3 -ml-3',
            segment.isCited
              ? 'border-l-blue-400 bg-blue-50/50'
              : 'border-l-transparent',
          )}
        >
          {segment.isCited && (
            <span className="inline-block mb-1 text-[10px] font-medium uppercase tracking-wide text-blue-500">
              Cited
            </span>
          )}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSanitize]}
          >
            {segment.content}
          </ReactMarkdown>
        </div>
      ))}
    </div>
  );
};

const formatTimestamp = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

const TranscriptCitationContent = ({
  matterId,
  conversationId,
  citation,
}: TranscriptCitationContentProps) => {
  const { data, isLoading, isError } = useGetTranscriptSegmentsQuery(
    {
      matterId,
      conversationId,
    },
    {
      skip: !conversationId,
    },
  );

  const targetLineId = citation.locator_type === 'line' ? citation.locator_start : null;
  const lineRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!targetLineId) {
      return;
    }

    const targetEl = lineRefs.current[targetLineId];
    if (!targetEl) {
      return;
    }

    targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [targetLineId, data?.length]);

  const hasTargetMatch =
    targetLineId !== null &&
    (data ?? []).some(
      (segment) => segment.id === targetLineId || segment.external_id === targetLineId,
    );

  if (isLoading) {
    return <LoaderState />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load transcript lines for this citation." />;
  }

  return (
    <div className="p-4">
      {targetLineId && !hasTargetMatch && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-700">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Precise transcript line `{targetLineId}` was not found.</span>
        </div>
      )}

      <div className="max-h-[70vh] space-y-2 overflow-y-auto pr-1">
        {data.map((segment) => {
          const isTarget =
            targetLineId !== null &&
            (segment.id === targetLineId || segment.external_id === targetLineId);

          return (
            <div
              key={segment.id}
              ref={(el) => {
                lineRefs.current[segment.id] = el;
                if (segment.external_id) {
                  lineRefs.current[segment.external_id] = el;
                }
              }}
              className={cn(
                'rounded-md border p-3 transition-colors',
                isTarget
                  ? 'border-blue-300 bg-blue-50 shadow-[0_0_0_1px_rgba(37,99,235,0.15)]'
                  : 'border-zinc-200 bg-white',
              )}
            >
              <div className="mb-1 flex items-center justify-between gap-2 text-[11px] text-zinc-500">
                <span>
                  {formatTimestamp(segment.start_time)} - {formatTimestamp(segment.end_time)}
                </span>
                {isTarget && <Badge variant="secondary">Cited line</Badge>}
              </div>
              <p className="text-xs leading-relaxed text-zinc-700 whitespace-pre-wrap">{segment.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ArtifactCitationContent = ({
  matterId,
  artifactId,
  citation,
  isResizing,
}: ArtifactCitationContentProps) => {
  const { data, isLoading, isError } = useGetArtifactUnpackedQuery(
    {
      matterId,
      artifactId,
    },
    {
      skip: !artifactId,
    },
  );

  if (isLoading) {
    return <LoaderState />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load artifact for this citation." />;
  }

  const digest = data.artifact_digest;

  if (data.artifact_type === ArtifactType.BLOB && data.blob) {
    const isPdfBlob =
      data.blob.file_mime_type === 'application/pdf' ||
      data.blob.file_extension?.toLowerCase() === 'pdf' ||
      data.blob.name.toLowerCase().endsWith('.pdf');

    return (
      <>
        <div className="border-b border-zinc-100 px-4 py-3">
          <p className="text-xs font-medium text-zinc-800">{data.blob.name}</p>
          {digest?.one_line_summary && (
            <p className="mt-1 text-xs text-zinc-500">{digest.one_line_summary}</p>
          )}
        </div>
        <BlobCitationContent
          matterId={matterId}
          blobId={data.blob.id}
          citation={citation}
          pdfUrl={data.blob.download_url}
          showPdfHighlighter={isPdfBlob}
          isResizing={isResizing}
        />
      </>
    );
  }

  if (
    data.artifact_type === ArtifactType.TRANSCRIPT &&
    data.transcript?.conversation?.id
  ) {
    return (
      <>
        <div className="border-b border-zinc-100 px-4 py-3">
          <p className="text-xs font-medium text-zinc-800">Transcript</p>
          {digest?.one_line_summary && (
            <p className="mt-1 text-xs text-zinc-500">{digest.one_line_summary}</p>
          )}
        </div>
        <TranscriptCitationContent
          matterId={matterId}
          conversationId={data.transcript.conversation.id}
          citation={citation}
        />
      </>
    );
  }

  if (data.artifact_type === ArtifactType.STAFF_NOTE && data.staff_note) {
    const noteUser =
      `${data.staff_note.user.first_name} ${data.staff_note.user.last_name}`.trim() ||
      data.staff_note.user.email;

    return (
      <div className="p-4">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
          <p className="text-xs font-medium text-zinc-800">{noteUser}</p>
          <p className="mt-1 text-xs text-zinc-600 whitespace-pre-wrap">{data.staff_note.content}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
        <p className="text-xs font-medium text-zinc-800">{digest?.title || 'Artifact'}</p>
        {digest?.three_line_summary && (
          <p className="mt-1 text-xs leading-relaxed text-zinc-600">{digest.three_line_summary}</p>
        )}
        {!digest?.three_line_summary && (
          <p className="mt-1 text-xs text-zinc-600">
            Precise preview is not available for this artifact type.
          </p>
        )}
      </div>
    </div>
  );
};

const TaskCitationContent = ({ matterId, taskId }: { matterId: string; taskId: string }) => {
  return (
    <div className="h-full overflow-y-auto">
      <TaskDetail
        taskId={taskId}
        matterId={matterId}
        onClose={() => undefined}
        isModal={false}
      />
    </div>
  );
};

const StaffNoteCitationContent = ({
  matterId,
  noteId,
}: {
  matterId: string;
  noteId: string;
}) => {
  const { data, isLoading, isError } = useGetStaffNoteQuery(
    {
      matterId,
      noteId,
    },
    {
      skip: !noteId,
    },
  );

  if (isLoading) {
    return <LoaderState />;
  }

  if (isError || !data) {
    return <ErrorState message="Unable to load staff note for this citation." />;
  }

  const userName =
    `${data.user.first_name} ${data.user.last_name}`.trim() || data.user.username;

  const timeAgo = formatDistanceToNow(new Date(data.created_at), {
    addSuffix: true,
  });

  return (
    <div className="p-4">
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-xs font-medium text-zinc-800">{userName}</p>
          <p className="text-[11px] text-zinc-500">{timeAgo}</p>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-zinc-700 whitespace-pre-wrap">
          {data.content}
        </p>
      </div>
    </div>
  );
};

export const WikiCitationDrawer = ({
  matterId,
  citation,
  onClose,
}: WikiCitationDrawerProps) => {
  const [width, setWidth] = useState(520);
  const [isResizing, setIsResizing] = useState(false);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    setIsResizing(true);
    startX.current = e.clientX;
    startWidth.current = width;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [width]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = startX.current - e.clientX;
      const newWidth = Math.min(Math.max(startWidth.current + delta, 360), 900);
      setWidth(newWidth);
    };

    const handleMouseUp = () => {
      if (!isDragging.current) return;
      isDragging.current = false;
      setIsResizing(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  if (!citation) {
    return null;
  }

  const sourceType = normalizeCitationSourceType(citation.source_type);
  const targetId = extractCitationTargetId(String(citation.source_id));

  const renderContent = () => {
    if (sourceType === 'artifact') {
      return (
        <ArtifactCitationContent
          matterId={matterId}
          artifactId={targetId}
          citation={citation}
          isResizing={isResizing}
        />
      );
    }

    if (sourceType === 'task') {
      return <TaskCitationContent matterId={matterId} taskId={targetId} />;
    }

    if (sourceType === 'staff_note') {
      return <StaffNoteCitationContent matterId={matterId} noteId={targetId} />;
    }

    return (
      <EmptyState
        message={`Unsupported citation source type: ${citation.source_type}`}
      />
    );
  };

  return (
    <aside
      className="relative flex h-full shrink-0 flex-col border-l border-zinc-100 bg-white"
      style={{ width }}
    >
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        onMouseDown={handleMouseDown}
        className="absolute inset-y-0 left-0 w-1 cursor-col-resize hover:bg-blue-400 active:bg-blue-500 transition-colors z-10"
      />

      <div className="flex items-center justify-between border-b border-zinc-100 px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Citation</p>
        <Button type="button" variant="ghost" size="icon" onClick={onClose} aria-label="Close citation panel">
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">{renderContent()}</div>
    </aside>
  );
};
